import { Fragment, useContext, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CurrencyContext from 'contexts/currencyContext'
import usePaymentLabels from 'hooks/usePaymentLabels'
import { nonNullable } from 'lib/array/arrayUtils'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { isKlarnaEnabled } from 'lib/payment/paymentUtils'
import { isStripePaymentElementKlarnaEnabled } from 'lib/payment/stripeUtils'
import Accordion from 'LuxKit/Accordion'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import {
  APPLE_PAY_PAYMENT_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  KLARNA_PAYMENT_TYPE,
  PAYPAL_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
} from 'constants/payment'
import { LOYALTY_PAYMENT_METHOD_EARN_MULTIPLIER } from 'loyalty/constants/loyaltyPaymentMethodEarn'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const messages = defineMessages({
  multiplierHeading: {
    id: 'A-BJukVM49UelxWVZ1bQ',
    defaultMessage: 'Payment methods that earn {multiplier}x {loyaltyProgramName} Points',
    description: 'Heading of the accordion in the loyalty points earning modal listing payment methods that earn a multiple of points',
  },
  baseHeading: {
    id: 'a6v9D_cN3lXSR8V_XYHU',
    defaultMessage: 'Payment methods that earn base {loyaltyProgramName} Points',
    description: 'Heading of the accordion in the loyalty points earning modal listing payment methods that earn the base rate of points',
  },
  debitCard: {
    id: '6oyVNzvynC5oJhc2aeW3',
    defaultMessage: 'Debit card',
    description: 'Payment method name in the loyalty points earning modal list for Visa and Mastercard debit cards',
  },
  prepaidCard: {
    id: 'qWvV-eYlN8kTUjJdinGJ',
    defaultMessage: 'Prepaid card',
    description: 'Payment method name in the loyalty points earning modal list for prepaid Visa and Mastercard cards',
  },
  amex: {
    id: 'ViuxNz-x7fapnwWXaJP6',
    defaultMessage: 'American Express',
    description: 'Payment method name in the loyalty points earning modal list for American Express cards',
  },
  payInFour: {
    id: 'YsTwf2TqMsHywND1VoZM',
    defaultMessage: 'Pay in 4',
    description: 'Payment method name in the loyalty points earning modal list for the PayPal Pay in 4 instalment option',
  },
  creditCards: {
    id: '9UB5EC8rB_IbsKLMeMtQ',
    defaultMessage: 'Credit cards',
    description: 'Payment method name in the loyalty points earning modal list for Visa and Mastercard credit cards',
  },
  link: {
    id: 'CVSZoqJbww43yXy1igpX',
    defaultMessage: 'Link',
    description: 'Payment method name in the loyalty points earning modal list for Stripe Link saved payment details',
  },
  credit: {
    id: 'jvGq_RPK-qOewBl8IkEb',
    defaultMessage: 'Luxury Escapes credit',
    description: 'Payment method name in the loyalty points earning modal list for Luxury Escapes account credit',
  },
})

interface PaymentMethodListItem {
  id: string
  label: string
  logos: Array<string>
}

const LogoImage = styled.img`
  height: ${rem(20)};
  width: ${rem(34)};
  object-fit: contain;
`

const PaymentMethodRow = styled(Group)`
  padding-block: ${rem(12)};
`

function PaymentMethodList({ items }: { items: Array<PaymentMethodListItem> }) {
  return <Group direction="vertical">
    {items.map((item, index) => (
      <Fragment key={item.id}>
        {index > 0 && <Divider kind="primary" />}
        <PaymentMethodRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <BodyText variant="medium" weight="bold">{item.label}</BodyText>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {item.logos.map(logo => <LogoImage key={logo} src={logo} alt="" />)}
          </Group>
        </PaymentMethodRow>
      </Fragment>
    ))}
  </Group>
}

/**
 * The two "which payment methods earn what" accordions in the Earning points modal. The lists are
 * hardcoded to match svc-lux-loyalty's low cost methods until they are served by an API.
 */
function LoyaltyPaymentMethodEarnAccordions() {
  const intl = useIntl()
  const { paymentMethodLabels } = usePaymentLabels()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const currency = useContext(CurrencyContext)
  // Klarna is only listed where checkout offers it
  const isKlarnaAvailable = isKlarnaEnabled(currency) || isStripePaymentElementKlarnaEnabled(currency)

  const { multiplierMethods, baseMethods } = useMemo(() => {
    const visaLogo = getSVGImageUrl('payments-visa')
    const mastercardLogo = getSVGImageUrl('payments-mastercard')
    const payPalLogo = getSVGImageUrl('payments-paypal-black')

    return {
      multiplierMethods: [
        { id: 'debit', label: intl.formatMessage(messages.debitCard), logos: [mastercardLogo, visaLogo] },
        { id: 'prepaid', label: intl.formatMessage(messages.prepaidCard), logos: [mastercardLogo, visaLogo] },
        { id: 'amex', label: intl.formatMessage(messages.amex), logos: [getSVGImageUrl('payments-amex')] },
        { id: 'paypal', label: paymentMethodLabels[PAYPAL_PAYMENT_TYPE], logos: [payPalLogo] },
        { id: 'pay-in-4', label: intl.formatMessage(messages.payInFour), logos: [payPalLogo] },
        { id: 'payid', label: paymentMethodLabels[AZUPAY_PAY_ID_PAYMENT_TYPE], logos: [getSVGImageUrl('payments-payId')] },
        { id: 'payto', label: paymentMethodLabels[STRIPE_CUSTOM_PAYTO_TYPE], logos: [getSVGImageUrl('payments-payto-logo')] },
      ],
      baseMethods: nonNullable([
        { id: 'apple-pay', label: paymentMethodLabels[APPLE_PAY_PAYMENT_TYPE], logos: [getSVGImageUrl('payments-applepay')] },
        { id: 'google-pay', label: paymentMethodLabels[GOOGLE_PAY_PAYMENT_TYPE], logos: [getSVGImageUrl('payments-googlepay')] },
        { id: 'credit-cards', label: intl.formatMessage(messages.creditCards), logos: [mastercardLogo, visaLogo] },
        { id: 'link', label: intl.formatMessage(messages.link), logos: [getSVGImageUrl('payments-link-dark')] },
        isKlarnaAvailable ? { id: 'klarna', label: paymentMethodLabels[KLARNA_PAYMENT_TYPE], logos: [getSVGImageUrl('payments-klarna')] } : undefined,
        { id: 'credit', label: intl.formatMessage(messages.credit), logos: [] },
      ]),
    }
  }, [intl, paymentMethodLabels, isKlarnaAvailable])

  return <Group direction="vertical" gap={8}>
    <Sheet background="light-background" border="light-foreground" borderRadius="M" padding="10 20">
      <Accordion
        heading={intl.formatMessage(messages.multiplierHeading, { multiplier: LOYALTY_PAYMENT_METHOD_EARN_MULTIPLIER, loyaltyProgramName })}
        size="large"
        hideDivider
      >
        <Sheet background="normal-background" borderRadius="M" padding="0 12 8">
          <PaymentMethodList items={multiplierMethods} />
        </Sheet>
      </Accordion>
    </Sheet>
    <Sheet border="light-foreground" borderRadius="M" padding="10 20">
      <Accordion heading={intl.formatMessage(messages.baseHeading, { loyaltyProgramName })} size="large" hideDivider>
        <Sheet padding="0 12">
          <PaymentMethodList items={baseMethods} />
        </Sheet>
      </Accordion>
    </Sheet>
  </Group>
}

export default LoyaltyPaymentMethodEarnAccordions
